import { IconCopy2Outline18 as CopyIcon } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"

import type { Option } from "@/screens/misc/design/widget-chat-typography-options"
import { isDevEnvironment } from "@/env"
import {
	FAQ_BOX_TONES,
	WIDGET_CHAT_TYPOGRAPHY_OPTIONS,
} from "@/screens/misc/design/widget-chat-typography-options"
import { NotFound } from "@/screens/not-found"

const ANSWER_HTML = `<p>A workspace admin or owner can enable it in <strong>Settings → Integrations → GitHub</strong>:</p>
<ol class="steps-list"><li>Install or open the GitHub integration and select the GitHub organization and repositories.</li><li>Enable the <strong>Issue sync</strong> option for the repositories you want.</li><li>Authorize the Linear GitHub app when prompted.</li></ol>
<p>Afterward, GitHub Issues and linked Linear issues sync bidirectionally. Your GitHub account may also need to be connected under <strong>Settings → Account → Connected accounts</strong>.</p>`

const ARTICLE_HTML = `<p>Stuut's call agent makes automated outbound phone calls at configured stages, for example 10 days past due, 21 days past due. It introduces itself with a configured name, references your company, and discusses open invoices with the customer.</p>
<p>The agent can:</p>
<ul><li>Provide the customer's total balance and overdue amount.</li><li>Handle common questions, like where to send a check or how to request an invoice copy.</li><li>Detect another language and switch automatically.</li><li>Transfer the call to a live person when a transfer number is configured.</li></ul>`

function UserBubble({ children }: { children: string }) {
	return (
		<div className="ml-auto w-fit min-w-0 max-w-[75%] rounded-md bg-gray-100 px-2.5 py-1.5 text-left !text-[0.94rem] leading-normal tracking-tight text-primary dark:bg-gray-800 dark:text-gray-100">
			{children}
		</div>
	)
}

export function WidgetFrame({ option }: { option: Option }) {
	return (
		<div className="w-[400px] flex-none overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
			<div className="flex h-11 items-center border-b border-gray-200 px-3.5 text-[13px] font-medium text-primary dark:border-gray-700/80">
				AI Agent
			</div>
			<div className="flex flex-col gap-4 px-3 py-4">
				<UserBubble>
					how to turn on github issue and linear issue sync?
				</UserBubble>
				<div className="flex flex-col px-1">
					<div
						className={option.body}
						dangerouslySetInnerHTML={{ __html: ANSWER_HTML }}
					/>
					<div className="mt-3 flex items-center gap-2 text-tertiary">
						<CopyIcon className="h-4 w-4 flex-none" />
					</div>
				</div>
			</div>
		</div>
	)
}

export function ArticleFrame({
	option,
	surface = FAQ_BOX_TONES[0]!.surface,
}: {
	option: Option
	surface?: string
}) {
	return (
		<div className="w-[560px] flex-none overflow-hidden rounded-xl bg-white p-7 shadow-lg ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
			<div className="text-[26px] font-semibold tracking-tight text-primary">
				AI Call Agent
			</div>
			<div className="mt-1 text-[15px] text-secondary">
				Review automated calls and the tasks they create.
			</div>
			<div className="mt-6 text-[17px] font-semibold text-primary">FAQ</div>
			<div className={cn("mt-3 rounded-lg border px-4 py-3.5", surface)}>
				<div className="text-[15px] font-medium text-primary">
					What does the AI call agent do?
				</div>
				<div
					className={cn(option.body, "mt-3")}
					dangerouslySetInnerHTML={{ __html: ARTICLE_HTML }}
				/>
			</div>
		</div>
	)
}

export function WidgetChatTypographyPage() {
	if (!isDevEnvironment) return <NotFound />

	return (
		<div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-900">
			<div className="mx-auto max-w-[1160px] px-6 py-10 pb-24">
				<div>
					<div className="text-lg font-semibold text-primary">
						Agent and article body, five typography options
					</div>
					<div className="mt-1 max-w-[660px] text-sm text-secondary">
						One class causes the grey on both surfaces:{" "}
						<code className="rounded bg-gray-200 px-1 py-0.5 text-xs dark:bg-gray-800">
							prose-p:text-secondary
						</code>{" "}
						in the editor typography variant. The widget bubble and the help
						center article both render through that variant, so each option
						below is drawn on both.
					</div>
				</div>

				<div className="mt-10 space-y-14">
					{WIDGET_CHAT_TYPOGRAPHY_OPTIONS.map((option) => (
						<div key={option.n}>
							<div className="mb-3">
								<div className="text-sm font-medium text-primary">
									{option.n}. {option.label}
								</div>
								<div className="mt-0.5 max-w-[660px] text-[13px] text-secondary">
									{option.note}
								</div>
								<div className="mt-1 font-mono text-xs text-tertiary">
									{option.spec}
								</div>
							</div>
							<div className="flex flex-wrap items-start gap-6">
								<WidgetFrame option={option} />
								<ArticleFrame option={option} />
							</div>
						</div>
					))}
				</div>

				<div className="mt-20">
					<div className="text-lg font-semibold text-primary">
						FAQ box, three tones
					</div>
					<div className="mt-1 max-w-[660px] text-sm text-secondary">
						Independent of the type. Each tone is drawn with option 3 body text
						so the fill is the only thing changing.
					</div>
					<div className="mt-8 space-y-12">
						{FAQ_BOX_TONES.map((tone) => (
							<div key={tone.n}>
								<div className="mb-3">
									<div className="text-sm font-medium text-primary">
										{tone.n}. {tone.label}
									</div>
									<div className="mt-0.5 max-w-[660px] text-[13px] text-secondary">
										{tone.note}
									</div>
								</div>
								<ArticleFrame
									option={WIDGET_CHAT_TYPOGRAPHY_OPTIONS[2]!}
									surface={tone.surface}
								/>
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	)
}
